import type {
  DocumentType,
  ServiceType,
} from "components/Documents/DocumentTypes";
import type { PaginatedResponse } from "types/DataTypes";

import { api } from "./api";

type GetDocumentsArgs = {
  fileId: string;
  serviceId?: string;
  serviceType?: ServiceType;
  searchTerm?: string;
  sortBy?: string;
  filters?: Record<string, string | string[]>;
  limit?: string;
  offset?: string;
  additionalParams?: Record<string, string>;
};

type GetDocumentsByClaimIdArgs = {
  claimId: string;
  searchTerm?: string;
  sortBy?: string;
  filters?: Record<string, string | string[]>;
  limit?: string;
  offset?: string;
};

type GetDocumentsByClientIdArgs = {
  clientId: string;
  searchTerm?: string;
  sortBy?: string;
  filters?: Record<string, string | string[]>;
  limit?: string;
  offset?: string;
};

type GetDocumentsByRREIdArgs = {
  rreId: string;
  searchTerm?: string;
  sortBy?: string;
  filters?: Record<string, string | string[]>;
  limit?: string;
  offset?: string;
};

type UploadDocumentArgs = {
  files: File[];
  fileId: string;
  serviceId?: string;
  serviceType?: ServiceType;
  documentType?: string;
  arScreeningDocumentType?: string;
  isCmsSubmissionDocument?: boolean;
  additionalParams?: Record<string, string>;
};

type UploadDocumentForClaimArgs = {
  files: File[];
  claimId: string;
  documentType?: string;
};

type UploadDocumentForRREArgs = {
  files: File[];
  rreId: string;
  documentType?: string;
};

type UpdateDocumentArgs = {
  documentId: number;
  data: Partial<DocumentType>;
};

type DeleteDocumentArgs = {
  documentId: number;
};

type BulkDownloadDocumentsArgs = {
  fileId?: number;
  clientId?: number;
  claimId?: number;
  documentIds?: number[];
};

type BulkDeleteDocumentsArgs = {
  documentIds: number[];
};

type GetBulkDownloadRequestArgs = {
  requestId: string;
};

const getServiceIdParam = (
  serviceType?: ServiceType,
  serviceId?: string,
  additionalParams?: Record<string, string>,
) => {
  if (!serviceId || !serviceType) return additionalParams || {};

  // for lcp and mcp, use cost_projection_id
  if (serviceType === "lcp" || serviceType === "mcp") {
    return { cost_projection_id: serviceId, ...additionalParams };
  }

  // special handling for liens: if lien_id is provided in additionalParams,
  // use it for filtering, but keep lien_file_id for uploads
  if (serviceType === "lien" && additionalParams?.lien_id) {
    // for uploads, we need both lien_id and lien_file_id
    // for filtering, we prioritize lien_id but include lien_file_id for upload compatibility
    const { lien_id, ...otherParams } = additionalParams;
    return { lien_id, lien_file_id: serviceId, ...otherParams };
  }

  // for other services, use their specific id field
  // these are the foreign keys in the documents table
  const serviceIdMap: Record<ServiceType, string> = {
    msa: "msa_id",
    arv: "amended_review_id",
    ars: "amended_review_screening_id",
    lcp: "cost_projection_id",
    mcp: "cost_projection_id",
    lien: "lien_file_id",
    docs: "document_management_file_id",
    CON: "miscellaneous_service_id",
    AUD: "miscellaneous_service_id",
  };

  return { [serviceIdMap[serviceType]]: serviceId, ...additionalParams };
};

const documentsApi = api.injectEndpoints({
  overrideExisting: true,
  endpoints: (build) => ({
    getDocuments: build.query<
      PaginatedResponse<DocumentType>,
      GetDocumentsArgs
    >({
      query: ({ fileId, searchTerm, sortBy, filters = {}, limit, offset }) => ({
        url: `api/v1/documents/`,
        method: "GET",
        params: {
          file_id: fileId,
          ...(searchTerm && { search: searchTerm }),
          ...(sortBy && { sort_by: sortBy }),
          ...(limit && { limit }),
          ...(offset && { offset }),
          ...filters,
        },
      }),
      providesTags: ["Documents"],
    }),

    getDocumentsByClaimId: build.query<
      PaginatedResponse<DocumentType>,
      GetDocumentsByClaimIdArgs
    >({
      query: ({
        claimId,
        searchTerm,
        sortBy,
        filters = {},
        limit,
        offset,
      }) => ({
        url: `api/v1/documents/`,
        method: "GET",
        params: {
          claim_id: claimId,
          ...(searchTerm && { search: searchTerm }),
          ...(sortBy && { sort_by: sortBy }),
          ...(limit && { limit }),
          ...(offset && { offset }),
          ...filters,
        },
      }),
      providesTags: ["ClaimDocuments"],
    }),

    getDocumentsByClientId: build.query<
      PaginatedResponse<DocumentType>,
      GetDocumentsByClientIdArgs
    >({
      query: ({
        clientId,
        searchTerm,
        sortBy,
        filters = {},
        limit,
        offset,
      }) => ({
        url: `api/v1/documents/`,
        method: "GET",
        params: {
          client_id: clientId,
          ...(searchTerm && { search: searchTerm }),
          ...(sortBy && { sort_by: sortBy }),
          ...(limit && { limit }),
          ...(offset && { offset }),
          ...filters,
        },
      }),
    }),

    getDocumentsByRREId: build.query<
      PaginatedResponse<DocumentType>,
      GetDocumentsByRREIdArgs
    >({
      query: ({ rreId, searchTerm, sortBy, filters = {}, limit, offset }) => ({
        url: `api/v1/documents/`,
        method: "GET",
        params: {
          rre_id: rreId,
          claim_id__isnull: true, // Ensures we only get RRE documents
          ...(searchTerm && { search: searchTerm }),
          ...(sortBy && { sort_by: sortBy }),
          ...(limit && { limit }),
          ...(offset && { offset }),
          ...filters,
        },
      }),
      providesTags: ["RREDocuments"],
    }),

    uploadDocument: build.mutation<DocumentType, UploadDocumentArgs>({
      query: ({
        files,
        fileId,
        serviceId,
        serviceType,
        documentType,
        arScreeningDocumentType,
        isCmsSubmissionDocument,
        additionalParams,
      }) => {
        const formData = new FormData();
        files.forEach((file) => {
          formData.append(file.name, file);
        });

        formData.append("file_id", fileId);

        const serviceIdParam = getServiceIdParam(
          serviceType,
          serviceId,
          additionalParams,
        );
        Object.entries(serviceIdParam).forEach(([key, value]) => {
          formData.append(key, value);
        });

        if (documentType) formData.append("type", documentType);

        if (arScreeningDocumentType)
          formData.append(
            "ar_screening_document_type",
            arScreeningDocumentType,
          );

        if (isCmsSubmissionDocument)
          formData.append("is_cms_submission_document", "true");

        return {
          url: "api/v1/documents/upload/",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: [
        "Documents",
        "MSA",
        "MSAValidation",
        "CostProjectionValidation",
        "Lien",
        "LienValidation",
        "FileValidation",
      ],
    }),
    uploadDocumentForClaim: build.mutation<
      DocumentType,
      UploadDocumentForClaimArgs
    >({
      query: ({ files, claimId, documentType }) => {
        const formData = new FormData();
        files.forEach((file) => {
          formData.append(file.name, file);
        });

        formData.append("claim_id", claimId);

        if (documentType) formData.append("type", documentType);

        return {
          url: "api/v1/documents/upload/",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["ClaimDocuments"],
    }),
    uploadDocumentForRRE: build.mutation<
      DocumentType,
      UploadDocumentForRREArgs
    >({
      query: ({ files, rreId, documentType }) => {
        const formData = new FormData();
        files.forEach((file) => {
          formData.append(file.name, file);
        });

        formData.append("rre_id", rreId);

        if (documentType) formData.append("type", documentType);

        return {
          url: "api/v1/documents/upload/",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["RREDocuments"],
    }),
    updateDocument: build.mutation<DocumentType, UpdateDocumentArgs>({
      query: ({ documentId, data }) => ({
        url: `api/v1/documents/${documentId}/`,
        method: "PATCH",
        body: data,
        headers: {
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: [
        "Documents",
        "MSAValidation",
        "CostProjectionValidation",
        "FileValidation",
        "LienValidation",
      ],
    }),
    deleteDocument: build.mutation<void, DeleteDocumentArgs>({
      query: ({ documentId }) => ({
        url: `api/v1/documents/${documentId}/`,
        method: "DELETE",
      }),
      invalidatesTags: [
        "Documents",
        "ClaimDocuments",
        "RREDocuments",
        "MSAValidation",
        "CostProjectionValidation",
        "FileValidation",
        "LienValidation",
      ],
    }),
    bulkDeleteDocuments: build.mutation<
      { deleted_count: number },
      BulkDeleteDocumentsArgs
    >({
      query: ({ documentIds }) => ({
        url: "api/v1/documents/bulk-delete/",
        method: "POST",
        body: { document_ids: documentIds },
      }),
      invalidatesTags: [
        "Documents",
        "ClaimDocuments",
        "RREDocuments",
        "MSAValidation",
        "CostProjectionValidation",
        "FileValidation",
        "LienValidation",
      ],
    }),
    bulkDownloadDocuments: build.mutation<
      { id: number; status: string; message: string },
      BulkDownloadDocumentsArgs
    >({
      query: ({ fileId, clientId, claimId, documentIds }) => ({
        url: "api/v1/bulk-document-download-requests/",
        method: "POST",
        body: {
          ...(fileId && { file: fileId }),
          ...(clientId && { client: clientId }),
          ...(claimId && { claim: claimId }),
          ...(documentIds && { document_ids: documentIds }),
        },
        headers: {
          "Content-Type": "application/json",
        },
      }),
    }),
    createPresignedUploadUrl: build.mutation<
      {
        upload_url: string;
        fields: Record<string, string>;
        s3_key: string;
      },
      {
        accessToken: string;
        intakeToken: string;
        data: {
          filename: string;
          intake_identifier: string;
          content_type?: string;
          extra_tags?: Record<string, string>;
        };
      }
    >({
      query: ({ accessToken, intakeToken, data }) => ({
        url: `api/v1/generate-presigned-url/`,
        method: "POST",
        body: data,
        headers: {
          Authorization: intakeToken
            ? `Intake-Bearer ${intakeToken}`
            : `Bearer ${accessToken}`,
        },
      }),
    }),
    // Deletes a not-yet-submitted intake/referral document from its S3 staging
    // area. The intake form only holds the s3_key client-side until submission,
    // and the backend ingests every object it finds under the intake prefix on
    // submit -- so removing a file from the drag-and-drop must delete the staged
    // S3 object, not just drop it from form state.
    deleteIntakeDocument: build.mutation<
      void,
      {
        accessToken: string;
        // nullable to match useIntakeToken() -- staff (Auth0) callers have no
        // intake token and fall back to the Bearer header below
        intakeToken: string | null;
        s3Key: string;
      }
    >({
      query: ({ accessToken, intakeToken, s3Key }) => ({
        url: `api/v1/delete-intake-document/`,
        method: "DELETE",
        body: { s3_key: s3Key },
        headers: {
          Authorization: intakeToken
            ? `Intake-Bearer ${intakeToken}`
            : `Bearer ${accessToken}`,
        },
      }),
    }),
    createDrawerPresignedUploadUrl: build.mutation<
      {
        upload_url: string;
        fields: Record<string, string>;
        s3_key: string;
        upload_session_id: string;
      },
      {
        filename: string;
        content_type?: string;
      }
    >({
      query: (data) => ({
        url: `api/v1/generate-drawer-presigned-url/`,
        method: "POST",
        body: data,
      }),
    }),
    // The Document row is created synchronously (201) so it appears immediately;
    // its file is encrypted off-thread by a Celery task shortly after.
    finalizeDrawerUpload: build.mutation<
      DocumentType,
      {
        s3_key: string;
        original_filename: string;
        file_id?: string;
        claim_id?: string;
        rre_id?: string;
        type?: string;
        is_cms_submission_document?: boolean;
        ar_screening_document_type?: string;
        msa_id?: string;
        msa_development_id?: string;
        amended_review_screening_id?: string;
        lien_file_id?: string;
        lien_id?: string;
        lien_part_id?: string;
        cost_projection_id?: string;
        miscellaneous_service_id?: string;
        document_management_file_id?: string;
        service_id?: string;
      }
    >({
      query: (data) => ({
        url: `api/v1/finalize-drawer-upload/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [
        "Documents",
        "ClaimDocuments",
        "RREDocuments",
        "MSA",
        "MSAValidation",
        "CostProjectionValidation",
        "Lien",
        "LienValidation",
        "FileValidation",
      ],
    }),
    getBulkDownloadRequest: build.query<
      {
        id: number;
        status: string;
        total_documents: number;
        processed_documents: number;
        expires_at: string;
        created: string;
        error_message?: string;
        file: number | null;
        client: number | null;
        claim: number | null;
      },
      GetBulkDownloadRequestArgs
    >({
      query: ({ requestId }) => ({
        url: `api/v1/bulk-document-download-requests/${requestId}/`,
        method: "GET",
      }),
    }),
    getBulkDownloadRequests: build.query<
      PaginatedResponse<{
        id: number;
        status: string;
        total_documents: number;
        processed_documents: number;
        expires_at: string;
        created: string;
        error_message?: string;
        file: number | null;
        client: number | null;
        claim: number | null;
      }>,
      {
        limit?: string;
        offset?: string;
        searchTerm?: string;
      }
    >({
      query: ({ limit, offset, searchTerm }) => ({
        url: "api/v1/bulk-document-download-requests/",
        method: "GET",
        params: {
          ...(limit && { limit }),
          ...(offset && { offset }),
          ...(searchTerm && { search: searchTerm }),
        },
      }),
      providesTags: ["BulkDownloads"],
    }),
  }),
});

export const {
  useGetDocumentsQuery,
  useGetDocumentsByClaimIdQuery,
  useGetDocumentsByClientIdQuery,
  useGetDocumentsByRREIdQuery,
  useUploadDocumentMutation,
  useUploadDocumentForClaimMutation,
  useUploadDocumentForRREMutation,
  useDeleteDocumentMutation,
  useBulkDeleteDocumentsMutation,
  useUpdateDocumentMutation,
  useBulkDownloadDocumentsMutation,
  useGetBulkDownloadRequestQuery,
  useGetBulkDownloadRequestsQuery,
  useCreatePresignedUploadUrlMutation,
  useDeleteIntakeDocumentMutation,
  useCreateDrawerPresignedUploadUrlMutation,
  useFinalizeDrawerUploadMutation,
} = documentsApi;

export default documentsApi;
